import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  Input,
  LanguageSelector,
  Badge,
  Separator,
  Progress,
} from "@tellescope/ui";
import { CheckCircle, Mail, ShieldCheck, Timer, AlertTriangle } from "lucide-react";
import { useAuth } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";

type MFAStep = "method-selection" | "code-input" | "success";
type MFAMethod = "email" | "recovery";

export const AdminMFAPage = () => {
  const { t } = useTranslation("auth");
  const [currentStep, setCurrentStep] = useState<MFAStep>("method-selection");
  const [currentMethod, setCurrentMethod] = useState<MFAMethod | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(300);

  const navigate = useNavigate();
  const { session, resetAuth, isAuthenticated } = useAuth();

  // Redirect if already authenticated and no MFA required
  if (isAuthenticated && !session?.userInfo?.requiresMFA) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const handleSendEmailCode = async () => {
    if (!session) return;

    setIsLoading(true);
    setError("");

    try {
      await session.api.users.generate_MFA_challenge({ method: "email" });
      setCurrentMethod("email");
      setCurrentStep("code-input");

      // Start countdown timer
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      // Check if this is a session expiration (401 Unauthorized)
      const isUnauthorized =
        err.status === 401 ||
        err.response?.status === 401 ||
        err.code === 401 ||
        (typeof err.message === "string" && err.message.includes("401")) ||
        (typeof err.message === "string" && err.message.toLowerCase().includes("unauthorized"));

      if (isUnauthorized) {
        resetAuth();
        navigate("/admin/login", { replace: true });
        return;
      }

      setError(err.message || t("Failed to send verification code. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseRecoveryCode = () => {
    setCurrentMethod("recovery");
    setCurrentStep("code-input");
  };

  const getExpectedCodeLength = () => {
    return currentMethod === "email" ? 8 : 16;
  };

  const handleSubmitCode = async () => {
    const expectedLength = getExpectedCodeLength();
    if (!session || code.length !== expectedLength) return;

    setIsLoading(true);
    setError("");

    try {
      const { authToken, user } = await session.api.users.submit_MFA_challenge({ code });

      session.setUserInfo(user);
      session.setAuthToken(authToken);

      setCurrentStep("success");

      // Brief success state before redirecting
      setTimeout(() => {
        navigate("/admin/dashboard");
      }, 1500);
    } catch (err: any) {
      // Check if this is a session expiration (401 Unauthorized)
      const isUnauthorized =
        err.status === 401 ||
        err.response?.status === 401 ||
        err.code === 401 ||
        (typeof err.message === "string" && err.message.includes("401")) ||
        (typeof err.message === "string" && err.message.toLowerCase().includes("unauthorized"));

      if (isUnauthorized) {
        // Session expired - redirect to login
        resetAuth();
        navigate("/admin/login", { replace: true });
        return;
      }

      // For other errors, show the validation error message
      setError(err.message || t("Invalid verification code. Please try again."));
      setCode(""); // Clear the code on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestartFlow = () => {
    setCurrentStep("method-selection");
    setCurrentMethod(null);
    setCode("");
    setError("");
    setTimeRemaining(300);
  };

  const handleCancelAndLogout = () => {
    resetAuth();
    navigate("/admin/login", { replace: true });
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getStepProgress = () => {
    switch (currentStep) {
      case "method-selection":
        return 25;
      case "code-input":
        return 75;
      case "success":
        return 100;
      default:
        return 0;
    }
  };

  const renderMethodSelection = () => (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
          <ShieldCheck className="w-6 h-6 text-blue-600" />
        </div>
        <h2 className="text-xl font-semibold">{t("Additional Security Required")}</h2>
        <p className="text-muted-foreground">{t("Choose how you authenticate")}</p>
      </div>

      <div className="space-y-4">
        <Button
          onClick={handleSendEmailCode}
          disabled={isLoading}
          className="w-full h-14 justify-start gap-3"
          variant="outline"
        >
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <Mail className="w-4 h-4 text-blue-600" />
          </div>
          <div className="text-left">
            <div className="font-medium">{t("Email Verification")}</div>
            <div className="text-sm text-muted-foreground">{t("Send a code to your email address")}</div>
          </div>
        </Button>

        <div className="relative">
          <Separator />
          <div className="absolute inset-0 flex items-center justify-center">
            <Badge variant="secondary" className="bg-background px-2">
              {t("OR")}
            </Badge>
          </div>
        </div>

        <Button onClick={handleUseRecoveryCode} variant="outline" className="w-full h-14 justify-start gap-3">
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-green-600" />
          </div>
          <div className="text-left">
            <div className="font-medium">{t("Recovery Code")}</div>
            <div className="text-sm text-muted-foreground">{t("Use a backup recovery code")}</div>
          </div>
        </Button>
      </div>
    </div>
  );

  const renderCodeInput = () => (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
          <CheckCircle className="w-6 h-6 text-green-600" />
        </div>
        <h2 className="text-xl font-semibold">{t("Enter Verification Code")}</h2>
        <p className="text-muted-foreground">
          {currentMethod === "email"
            ? t("Enter the 8-digit code sent to your email")
            : t("Enter your 16-character recovery code")}
        </p>
      </div>

      {currentMethod === "email" && timeRemaining > 0 && (
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Timer className="w-4 h-4" />
          <span>
            {t("Code expires in")} {formatTime(timeRemaining)}
          </span>
        </div>
      )}

      <div className="flex justify-center">
        {currentMethod === "email" ? (
          <InputOTP
            maxLength={8}
            value={code}
            onChange={setCode}
            onComplete={handleSubmitCode}
            disabled={isLoading || timeRemaining === 0}
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
              <InputOTPSlot index={6} />
              <InputOTPSlot index={7} />
            </InputOTPGroup>
          </InputOTP>
        ) : (
          <div className="w-full max-w-sm space-y-4">
            <Input
              type="text"
              placeholder={t("Enter recovery code")}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={isLoading}
              maxLength={16}
              className="text-center text-lg tracking-wider font-mono"
              autoFocus
            />
            <Button onClick={handleSubmitCode} disabled={isLoading || code.length !== 16} className="w-full" size="lg">
              {isLoading ? t("Verifying...") : t("Verify Code")}
            </Button>
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}

      {currentMethod === "email" && timeRemaining === 0 && (
        <div className="text-center space-y-4">
          <p className="text-sm text-muted-foreground">{t("Your verification code has expired")}</p>
          <Button onClick={handleRestartFlow} variant="outline">
            {t("Request New Code")}
          </Button>
        </div>
      )}

      <div className="flex justify-center">
        <Button onClick={handleRestartFlow} variant="ghost" size="sm">
          {t("Back to method selection")}
        </Button>
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div className="space-y-6 text-center">
      <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
        <CheckCircle className="w-8 h-8 text-green-600" />
      </div>
      <div className="space-y-2">
        <h2 className="text-xl font-semibold text-green-600">{t("Verification Complete!")}</h2>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case "method-selection":
        return renderMethodSelection();
      case "code-input":
        return renderCodeInput();
      case "success":
        return renderSuccess();
      default:
        return renderMethodSelection();
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
      <div className="absolute top-4 right-4">
        <LanguageSelector size="sm" />
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          <div className="space-y-2">
            <CardTitle className="text-2xl font-bold">{t("Security Verification")}</CardTitle>
            <CardDescription>{t("Complete multi-factor authentication to continue")}</CardDescription>
          </div>

          <div className="space-y-2">
            <Progress value={getStepProgress()} className="h-2" />
            <div className="text-xs text-muted-foreground">
              {t("Step {{stepNumber}}", {
                stepNumber: currentStep === "method-selection" ? 1 : currentStep === "code-input" ? 2 : 3,
              })}
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {renderCurrentStep()}

          <div className="mt-8 pt-6 border-t">
            <Button onClick={handleCancelAndLogout} variant="ghost" size="sm" className="w-full">
              {t("Cancel and Logout")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
